import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { MessageSquare, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_STATUSES = ['unread', 'read', 'replied', 'closed'];

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (contactId, status) => {
    try {
      await axios.put(`${API}/admin/contacts/${contactId}/status?status=${status}`);
      toast.success('Message status updated');
      fetchContacts();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating status');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout title="Contact Messages">
      <div data-testid="admin-contacts">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-20 bg-white">
            <MessageSquare size={48} className="mx-auto text-[#E5E7EB] mb-4" />
            <h3 className="font-semibold text-[#1A1A1A] mb-2">No messages yet</h3>
            <p className="text-[#6B7280]">Contact messages will appear here</p>
          </div>
        ) : (
          <div className="bg-white shadow-sm overflow-x-auto">
            <table className="w-full" data-testid="contacts-table">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#F9F7F2]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">From</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Subject</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-[#6B7280]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr 
                    key={contact.id} 
                    className={`border-b border-[#E5E7EB] ${contact.status === 'unread' ? 'bg-[#8B5E3C]/5' : ''}`}
                  >
                    <td className="py-3 px-4">
                      <div>
                        <p className={`font-medium ${contact.status === 'unread' ? 'text-[#1A1A1A]' : 'text-[#6B7280]'}`}>
                          {contact.name}
                        </p>
                        <p className="text-xs text-[#6B7280]">{contact.email}</p>
                      </div>
                    </td>
                    <td className={`py-3 px-4 ${contact.status === 'unread' ? 'font-medium' : 'text-[#6B7280]'}`}>
                      {contact.subject}
                    </td>
                    <td className="py-3 px-4 text-sm text-[#6B7280]">{formatDate(contact.created_at)}</td>
                    <td className="py-3 px-4">
                      <Select
                        value={contact.status}
                        onValueChange={(value) => updateStatus(contact.id, value)}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CONTACT_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            setSelectedContact(selectedContact?.id === contact.id ? null : contact);
                            if (contact.status === 'unread') {
                              updateStatus(contact.id, 'read');
                            }
                          }}
                          data-testid={`view-contact-${contact.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Contact Details */}
            {selectedContact && (
              <div className="border-t border-[#E5E7EB] p-6 bg-[#F9F7F2]" data-testid="contact-details">
                <h3 className="font-semibold text-[#1A1A1A] mb-4">Message Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-[#6B7280] mb-2">Contact Information</h4>
                    <p className="text-[#1A1A1A] font-medium">{selectedContact.name}</p>
                    <p className="text-sm text-[#6B7280]">{selectedContact.email}</p>
                    {selectedContact.phone && (
                      <p className="text-sm text-[#6B7280]">{selectedContact.phone}</p>
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-[#6B7280] mb-2">Subject</h4>
                    <p className="text-[#1A1A1A] font-medium">{selectedContact.subject}</p>
                  </div>
                  <div className="md:col-span-2">
                    <h4 className="text-sm font-medium text-[#6B7280] mb-2">Message</h4>
                    <p className="text-[#1A1A1A] bg-white p-4 rounded whitespace-pre-wrap">
                      {selectedContact.message}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <Button asChild>
                      <a href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}>
                        Reply via Email
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
